import React, { useState, useEffect, useRef } from 'react';
import CrimsonLogo from './CrimsonLogo';

const STATUS_WORDS = [
  'indexing', 'scanning', 'computing', 'cogitating', 'vectorizing',
  'synthesizing', 'enumerating', 'crystallizing', 'decomposing',
  'optimizing', 'evaluating', 'generating', 'resolving', 'analyzing',
];

function ElapsedTimer() {
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    startRef.current = Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return (
    <span style={{ fontVariantNumeric: 'tabular-nums', color: '#94a3b8', fontSize: 22, fontWeight: 600 }}>
      {mins}:{String(secs).padStart(2, '0')}
    </span>
  );
}

function TypewriterWord() {
  const [wordIdx, setWordIdx] = useState(() => Math.floor(Math.random() * STATUS_WORDS.length));
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const word = STATUS_WORDS[wordIdx];

  useEffect(() => {
    let timeout;
    if (!deleting) {
      if (charIdx < word.length) {
        timeout = setTimeout(() => setCharIdx(c => c + 1), 70 + Math.random() * 40);
      } else {
        // Pause at full word, then start deleting
        timeout = setTimeout(() => setDeleting(true), 1800);
      }
    } else {
      if (charIdx > 0) {
        timeout = setTimeout(() => setCharIdx(c => c - 1), 35);
      } else {
        // Pick next word (avoid repeat)
        setDeleting(false);
        setWordIdx(prev => {
          let next;
          do { next = Math.floor(Math.random() * STATUS_WORDS.length); } while (next === prev);
          return next;
        });
      }
    }
    return () => clearTimeout(timeout);
  }, [charIdx, deleting, word.length]);

  return (
    <span style={{ color: '#f43f5e', fontWeight: 600, fontSize: 18, letterSpacing: 1.5, textTransform: 'lowercase' }}>
      {word.slice(0, charIdx)}
      <span style={{ opacity: 0.7, animation: 'cs-blink 0.8s step-end infinite' }}>_</span>
    </span>
  );
}

const STATUS_COLORS = {
  queued:  '#64748b',
  running: '#61dafb',
  done:    '#4ade80',
  partial: '#f59e0b', // 0.7: some MatterGen jobs failed, the rest were kept
  error:   '#f87171',
};

const STATUS_LABELS = {
  queued:  'Queued',
  running: 'Running',
  done:    'Done',
  partial: 'Partial',
  error:   'Failed',
};

function LoadingOverlay({ loading, loadingMessage, progress, onCancel }) {
  if (!loading) return null;
  // Part 1a (contract 1.5 step 6): progress is keyed by row id; two rows may share a label.
  const bars = Object.entries(progress || {});
  return (
    <>
      {/* Blink animation for cursor */}
      <style>{`@keyframes cs-blink { 0%, 100% { opacity: 0.7; } 50% { opacity: 0; } }`}</style>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div style={{
          color: '#fff',
          padding: '36px 40px 28px',
          background: '#0f1923',
          borderRadius: 16,
          minWidth: 460,
          maxWidth: 560,
          border: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          {/* Logo + brand */}
          <CrimsonLogo size={72} animated />
          <div style={{ marginTop: 14, fontSize: 24, fontWeight: 700, color: '#f43f5e', letterSpacing: 0.5 }}>
            CrimsonScan
          </div>

          {/* Typewriter status word */}
          <div style={{ marginTop: 12, minHeight: 28 }}>
            <TypewriterWord />
          </div>

          {/* Timer + status message */}
          <div style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid #1e293b',
          }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#94a3b8' }}>{loadingMessage || 'Working...'}</span>
            <ElapsedTimer />
          </div>

          {/* Progress bars */}
          {bars.length > 0 && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
              {bars.map(([barKey, p]) => {
                const pct = p.total > 0 ? Math.round((p.done / p.total) * 100) : 0;
                const status = p.status || 'running';
                const barColor = STATUS_COLORS[status] || '#61dafb';
                return (
                  <div key={barKey} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                      <span style={{ color: '#e2e8f0' }}>{p.label}</span>
                      <span style={{ color: '#94a3b8' }}>
                        {p.done}/{p.total}
                        <span style={{ color: barColor, marginLeft: 8, fontSize: 11, textTransform: 'uppercase', fontWeight: 600 }}>
                          {STATUS_LABELS[status] || status}
                        </span>
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: barColor,
                        borderRadius: 3,
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Cancel button */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              onClick={onCancel}
              style={{
                background: 'transparent',
                border: '1px solid #f87171',
                color: '#f87171',
                padding: '6px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoadingOverlay;
